import React, { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { isSunday, isSaturday, isMonday, format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLilius } from "use-lilius";

import { Button } from "src/components/ui/button";
import { Card } from "src/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import Dropdown from "src/components/Dropdown";
import { getAppDataGetAppDataQueryOptions } from "src/model/app-data";
import { TradeResponse } from "src/model/client.schemas";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { normalizeTrades } from "@/util/unifiedTradesApi";
import {
  filterTradesForAccount,
  calculateReturnsByDate,
  groupTradesByDay,
  PartialProfitData,
} from "src/util/trades";
import { useAccount } from "src/context/AccountContext";
import { getGetAppDataWithParamsQueryOptions } from "src/util/query_client";
import LoadingOverlay from "src/components/LoadingOverlay";
import { useSuspenseQuery } from "@tanstack/react-query";
import { formatPercentage, formatProfitFactor } from "src/util/formatters";
import { usePrivacyMode } from "src/context/PrivacyModeContext";
import { useUserTimezone } from "@/context/AppConfigContext";
import TradeDialog from "@/components/TradeDialog";
import { useTradesUpdateTrade } from "@/model/trades";
import { useUpdateTrade } from "@/model_v2/trades";
import { getSetups } from "src/util/trades";
import { MoneyDisplay } from "src/components/MoneyDisplay";
import TradesStatsPanel from "@/components/TradesStatsPanel";
import { FilterManager, FilterState } from "src/util/attribute_filter";
import { filterConfig } from "src/util/trades_table_config";
import { TradeOrActivity } from "@/util/activity_util";

const PROFIT_CLASS = "text-pnl-positive";
const LOSS_CLASS = "text-pnl-negative";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const Route = createFileRoute("/_app/calendar")({
  component: CalendarPageWrapper,
  staticData: {
    title: "Calendar",
  },
  pendingComponent: () => <LoadingOverlay isLoading={true} />,
});

interface MonthPnlProps {
  trades_by_date: { [key: string]: PartialProfitData[] };
  viewing: Date;
  className?: string;
}

const MonthPnl: React.FC<MonthPnlProps> = ({
  trades_by_date,
  viewing,
  className,
}) => {
  const viewingMonth = viewing.getMonth();
  const viewingYear = viewing.getFullYear();

  const monthTotal = Object.entries(trades_by_date)
    .filter(([date]) => {
      // Parse date string directly (YYYY-MM-DD) to avoid timezone conversion issues
      const [year, month] = date.split('-').map(Number);
      return month - 1 === viewingMonth && year === viewingYear;
    })
    .flatMap(([, partialProfitData]) => partialProfitData)
    .reduce((sum, data) => sum + data.pnl, 0);

  return (
    <div
      className={`flex gap-4 text-xl font-medium lg:hidden ${className || ""}`}
    >
      <span>Month Pnl</span>
      <span className={`${monthTotal >= 0 ? PROFIT_CLASS : LOSS_CLASS}`}>
        <MoneyDisplay value={monthTotal} />
      </span>
    </div>
  );
};

interface CalendarHeaderProps {
  tradeYears: number[];
  viewing: Date;
  setViewing: (viewing: Date) => void;
  viewMonth: (month: number) => void;
  viewYear: (year: number) => void;
  trades_by_date: { [key: string]: PartialProfitData[] };
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  tradeYears,
  setViewing,
  viewing,
  viewMonth,
  viewYear,
  trades_by_date,
}) => {
  const changeMonth = (delta: number) => {
    const newDate = new Date(viewing);
    newDate.setMonth(newDate.getMonth() + delta);
    viewMonth(newDate.getMonth());
    if (newDate.getFullYear() !== viewing.getFullYear()) {
      viewYear(newDate.getFullYear());
    }
    setViewing(newDate);
  };

  return (
    <div>
      <div className="flex flex-row flex-wrap items-start justify-between">
        <div className="mb-4 flex flex-1 gap-2">
          <Button
            className="select rounded-full border border-input p-2.5"
            onClick={() => changeMonth(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="w-[120px] sm:w-[135px]">
            <Dropdown
              values={MONTHS.map((month, index) => [index.toString(), month])}
              placeholder="Select Month"
              selectedValue={viewing.getMonth().toString()}
              onValueChange={(m: string) => viewMonth(Number(m))}
              className="card max-h-fit"
            />
          </div>
          <Button
            className="select rounded-full border border-input p-2.5"
            onClick={() => changeMonth(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <MonthPnl
          trades_by_date={trades_by_date}
          viewing={viewing}
          className="hidden md:flex"
        />
        <div className="flex flex-1 justify-end">
          <div className="w-[90px]">
            <Dropdown
              values={tradeYears.map((year) => [
                year.toString(),
                year.toString(),
              ])}
              placeholder={viewing.getFullYear().toString()}
              selectedValue={viewing.getFullYear().toString()}
              onValueChange={(y: string) => viewYear(Number(y))}
              className="card"
            />
          </div>
        </div>
      </div>
      <MonthPnl
        trades_by_date={trades_by_date}
        viewing={viewing}
        className="flex justify-center md:hidden"
      />
    </div>
  );
};

interface WeekSummaryCardProps {
  week: Date[];
  trades_by_date: { [key: string]: PartialProfitData[] };
  viewingYear: number;
  viewingMonth: number;
}

const WeekSummaryCard: React.FC<WeekSummaryCardProps> = ({
  week,
  trades_by_date,
  viewingYear,
  viewingMonth,
}) => {
  const { privacyMode } = usePrivacyMode();
  const weekPartialProfitData = week.flatMap(
    (day) => trades_by_date[getDateKey(day, viewingYear, viewingMonth)] || [],
  );

  // Deduplicate PartialProfitData based on trade.id
  const weekClosedTradesMap = new Map<string, TradeResponse>();
  weekPartialProfitData.forEach((data) => {
    if (!weekClosedTradesMap.has(data.trade.id) && data.trade.close_date) {
      weekClosedTradesMap.set(data.trade.id, data.trade);
    }
  });
  const weekClosedTrades = Array.from(weekClosedTradesMap.values());

  // Calculate Net Return
  const netReturn = weekPartialProfitData.reduce(
    (sum, data) => sum + data.pnl,
    0,
  );
  const profitOrLossClass = netReturn >= 0 ? PROFIT_CLASS : LOSS_CLASS;

  const winningClosedTrades = weekClosedTrades.filter(
    (data) => (data.profit_or_loss ?? 0) >= 0,
  ).length;
  const totalClosedTrades = weekClosedTrades.length;
  const winRateValue =
    totalClosedTrades > 0 ? winningClosedTrades / totalClosedTrades : null;

  // Calculate Profit Factor
  const grossProfit = weekPartialProfitData
    .filter((data) => data.pnl > 0)
    .reduce((sum, data) => sum + data.pnl, 0);
  const grossLoss = Math.abs(
    weekPartialProfitData
      .filter((data) => data.pnl < 0)
      .reduce((sum, data) => sum + data.pnl, 0),
  );
  const profitFactorValue = grossLoss > 0 ? grossProfit / grossLoss : null;

  return (
    <Card className="col-span-1 grid grid-cols-1 p-1 text-center dark:border-0 dark:bg-calendar-cell sm:col-span-3 sm:grid-cols-3 md:min-w-[200px] md:p-4">
      <div className="flex w-full flex-col items-start justify-between">
        <h3
          className={`overflow-hidden text-ellipsis whitespace-nowrap font-medium tracking-tight ~text-xs/xl first:mt-0 ${profitOrLossClass}`}
        >
          <MoneyDisplay value={netReturn} />
        </h3>
        <p className="text-xs">Total</p>
      </div>
      <div className="flex flex-col items-center justify-between max-sm:hidden">
        <h3 className="font-medium tracking-tight ~text-base/2xl first:mt-0">
          {formatPercentage(winRateValue ?? 0, undefined, "auto")}
        </h3>
        <p className="text-xs">Win Rate</p>
      </div>
      <div className="flex flex-col items-end justify-between text-end max-sm:hidden">
        <h3 className="font-medium tracking-tight ~text-base/2xl first:mt-0">
          {formatProfitFactor(profitFactorValue)}
        </h3>
        <p className="text-xs">Profit Factor</p>
      </div>
    </Card>
  );
};

interface DayPopoverProps {
  day: Date;
  partialProfitData: PartialProfitData[];
}

const DayPopover: React.FC<DayPopoverProps> = ({ day, partialProfitData }) => {
  const [selectedTrade, setSelectedTrade] = useState<TradeResponse | null>(
    null,
  );
  const { privacyMode } = usePrivacyMode();

  const useV2Api = useFeatureFlag("use_v2_imports");
  const updateTradeMutationV1 = useTradesUpdateTrade();
  const updateTradeMutationV2 = useUpdateTrade();
  const updateTradeMutation = useV2Api ? updateTradeMutationV2 : updateTradeMutationV1;
  return (
    <>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute left-0 top-0 h-full w-full cursor-pointer" />
          </TooltipTrigger>
          <TooltipContent
            className="w-80 p-0 dark:border-[#333438] dark:bg-medium-black"
            sideOffset={5}
          >
            <h3 className="mb-4 ml-3 mt-2 text-lg font-semibold dark:text-gray3">
              {format(day, "MMM d, yyyy")}
            </h3>
            <Table
              containerClassName="border dark:border-gray3/5 max-h-[300px]"
              className="dark:bg-medium-gray dark:text-white"
            >
              <TableHeader className="sticky top-0 bg-white dark:bg-medium-black">
                <TableRow className="bg-purple2/10 text-black hover:bg-purple2/10 dark:border-gray3 dark:border-opacity-5 dark:bg-light-gray">
                  <TableHead className="h-10 px-4 text-left">Ticker</TableHead>
                  <TableHead className="h-10 px-4 text-right">PnL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partialProfitData.map((data, index) => (
                  <TableRow
                    key={index}
                    onClick={() => {
                      setSelectedTrade(data.trade);
                    }}
                    className="dark:border-gray3 dark:border-opacity-5 dark:hover:bg-light-gray"
                  >
                    <TableCell className="px-4 py-2">
                      {data.trade.ticker}
                    </TableCell>
                    <TableCell
                      className={`px-4 py-2 text-right ${(data.pnl || 0) > 0 ? PROFIT_CLASS : LOSS_CLASS}`}
                    >
                      {(data.pnl || 0) > 0 && privacyMode ? (
                        "Win"
                      ) : (data.pnl || 0) < 0 && privacyMode ? (
                        "Loss"
                      ) : (data.pnl || 0) === 0 && privacyMode ? (
                        "Scratch"
                      ) : (
                        <MoneyDisplay value={data.pnl || 0} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {selectedTrade && (
        <TradeDialog
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
          updateTradeMutation={updateTradeMutation}
          userSetups={getSetups(partialProfitData.map((data) => data.trade))}
        />
      )}
    </>
  );
};

// Helper to get date key in user timezone from calendar cell
// The calendar cell shows a day number (1-31) for a given viewing month/year
// We construct the date string directly to avoid browser timezone issues
function getDateKey(day: Date, viewingYear: number, viewingMonth: number): string {
  const dayOfMonth = day.getDate();
  const cellMonth = day.getMonth();

  // Handle overflow days from adjacent months shown in the calendar grid
  let year = viewingYear;
  let month = cellMonth;

  // If cell's month doesn't match viewing month, it's an overflow day
  if (cellMonth !== viewingMonth) {
    month = cellMonth;
    // Adjust year if we're showing Dec days while viewing Jan, or Jan days while viewing Dec
    if (viewingMonth === 0 && cellMonth === 11) {
      year = viewingYear - 1;
    } else if (viewingMonth === 11 && cellMonth === 0) {
      year = viewingYear + 1;
    }
  }

  // Construct date string: YYYY-MM-DD
  const monthStr = String(month + 1).padStart(2, '0');
  const dayStr = String(dayOfMonth).padStart(2, '0');
  return `${year}-${monthStr}-${dayStr}`;
}

interface CalendarBodyProps {
  calendar: Date[][][];
  trades_by_date: { [key: string]: PartialProfitData[] };
  viewingMonth: number;
  viewingYear: number;
}

const CalendarBody: React.FC<CalendarBodyProps> = ({
  calendar,
  trades_by_date,
  viewingMonth,
  viewingYear,
}) => {
  // Check if there are any weekend trades in the current month
  const hasWeekendTrades = React.useMemo(() => {
    let hasWeekendTrades = false;
    calendar[0].map((week, _) => {
      week.map((day) => {
        const isWeekend = isSaturday(day) || isSunday(day);
        const dateKey = getDateKey(day, viewingYear, viewingMonth);
        const dayPartialProfitData = trades_by_date[dateKey] || [];
        if (
          day.getMonth() === viewingMonth &&
          isWeekend &&
          dayPartialProfitData.length > 0
        ) {
          hasWeekendTrades = true;
        }
      });
    });
    return hasWeekendTrades;
  }, [calendar, trades_by_date, viewingMonth, viewingYear]);

  const { privacyMode } = usePrivacyMode();

  const dayReturnDiv = (dayPartialProfitData: PartialProfitData[]) => {
    const count = dayPartialProfitData.length;
    const total = dayPartialProfitData.reduce((acc, data) => acc + data.pnl, 0);

    if (count > 0) {
      if (privacyMode) {
        return <div className="text-nowrap ~text-xs/lg">&nbsp;</div>;
      }
      return (
        <div
          className={`text-nowrap text-right ~text-xs/lg ${total >= 0 ? PROFIT_CLASS : LOSS_CLASS}`}
        >
          <MoneyDisplay value={total} />
        </div>
      );
    }
    return null;
  };

  const dayCountsDiv = (dayPartialProfitData: PartialProfitData[]) => {
    const countWins = dayPartialProfitData.filter(
      (data) => data.pnl > 0,
    ).length;
    const countLosses = dayPartialProfitData.filter(
      (data) => data.pnl < 0,
    ).length;

    // In privacy mode, always show win/loss counts if there are any trades
    const displayClass =
      privacyMode && (countWins > 0 || countLosses > 0)
        ? "flex justify-end gap-1 text-center md:gap-2"
        : "mx-auto hidden justify-end gap-1 text-center sm:flex md:gap-2";

    return (
      <div className={displayClass}>
        {countWins > 0 && (
          <span className={`text-xs ${PROFIT_CLASS}`}>{countWins} W</span>
        )}
        {countLosses > 0 && (
          <span className={`text-xs ${LOSS_CLASS}`}>{countLosses} L</span>
        )}
      </div>
    );
  };
  const daysToShow = hasWeekendTrades ? WEEKDAYS : WEEKDAYS.slice(0, -2);

  return (
    <div
      className={`mt-4 grid w-full gap-1 md:gap-2 ${
        hasWeekendTrades
          ? "min-w-[400px] grid-cols-8 sm:grid-cols-10"
          : "grid-cols-6 sm:grid-cols-8"
      }`}
    >
      {daysToShow.map((day) => (
        <div key={day} className="mb-3 h-6 text-center font-medium ~text-lg/xl">
          {day}
        </div>
      ))}
      {calendar[0].map((week, weekIndex) => {
        // Skip weeks where all visible days are from other months
        const allDaysFromOtherMonths = week.every((day) => {
          // If the day is from another month, skip it
          if (day.getMonth() !== viewingMonth) {
            return true;
          }
          // If the day is a weekend and there are no trades on it, skip it
          const isWeekend = isSaturday(day) || isSunday(day);
          const dateKey = getDateKey(day, viewingYear, viewingMonth);
          const dayPartialProfitData = trades_by_date[dateKey] || [];
          const dayTrades = dayPartialProfitData.map((data) => data.trade);
          if (isWeekend && dayTrades.length === 0) {
            return true;
          }
          return false;
        });
        if (allDaysFromOtherMonths) {
          return null;
        }

        return [
          week.map((day, dayIndex) => {
            if ((isSaturday(day) || isSunday(day)) && !hasWeekendTrades) {
              return (
                <React.Fragment
                  key={`${weekIndex}-${dayIndex}`}
                ></React.Fragment>
              );
            }
            const dateKey = getDateKey(day, viewingYear, viewingMonth);
            const dayPartialProfitData = trades_by_date[dateKey] || [];
            return (
              <Card
                key={`${weekIndex}-${dayIndex}`}
                className={`col-span-1 flex h-full min-h-12 w-full min-w-12 flex-col justify-between overflow-hidden p-1 ~text-xs/xl dark:border-0 dark:bg-calendar-cell md:p-3 ${isMonday(day) ? "col-start-1" : ""} relative`}
              >
                <div
                  className={
                    viewingMonth === day.getMonth() ? "" : "opacity-50"
                  }
                >
                  <div className="text-right">{format(day, "dd")}</div>
                  {dayReturnDiv(dayPartialProfitData)}
                  {dayCountsDiv(dayPartialProfitData)}
                </div>
                {dayPartialProfitData.length > 0 && (
                  <DayPopover
                    day={day}
                    partialProfitData={dayPartialProfitData}
                  />
                )}
              </Card>
            );
          }),
          <WeekSummaryCard
            key={`${week}`}
            week={week}
            trades_by_date={trades_by_date}
            viewingYear={viewingYear}
            viewingMonth={viewingMonth}
          />,
        ];
      })}
    </div>
  );
};

function CalendarPageWrapper() {
  return <CalendarPage />;
}

function CalendarPage() {
  const userTimezone = useUserTimezone();
  const { selectedAccount } = useAccount();
  const useV2Api = useFeatureFlag("use_v2_imports");

  // Choose query options based on feature flag
  // Calendar doesn't need attributes - uses compact response
  const query = useV2Api
    ? getGetAppDataWithParamsQueryOptions()
    : getAppDataGetAppDataQueryOptions();
  // @ts-ignore
  const { data: response } = useSuspenseQuery(query);

  const { calendar, setViewing, viewing, viewMonth, viewYear } = useLilius({
    weekStartsOn: 1,
    viewing: new Date(),
  });

  // Create a FilterManager that filters trades by the current month/year
  const [filterState, setFilterState] = useState<FilterState>({});

  // Create a filter manager that will filter trades by PnL timestamps in the current month/year
  const filterManager = useMemo(() => {
    return new FilterManager(filterConfig, filterState, setFilterState);
  }, [filterState, viewing, userTimezone]);

  // Wait for the selected account to be set since we have accounts in the response.
  if (!selectedAccount && (response.data.account_data?.length ?? 0) > 0) {
    return null;
  }

  // Normalize trades from either API version
  const normalizedTrades = normalizeTrades(response?.data.all_trades);
  
  const accountTrades = filterTradesForAccount(
    normalizedTrades,
    selectedAccount,
  );

  // Convert accountTrades to TradeOrActivity[] for TradesStatsPanel
  const tableItems = accountTrades as TradeOrActivity[];

  const trades_by_date = groupTradesByDay(accountTrades, userTimezone);
  const returns_by_date = calculateReturnsByDate(accountTrades, userTimezone);
  const currentYear = new Date().getFullYear();
  const earliestYear =
    Object.keys(returns_by_date).length > 0
      ? Math.min(
          ...Object.keys(returns_by_date).map((date) =>
            new Date(date).getFullYear(),
          ),
        )
      : currentYear;

  // Always include at least the current year in the tradeYears array
  // This ensures the dropdown always shows the current year even if there are no trades
  const tradeYears =
    Object.keys(returns_by_date).length > 0
      ? Array.from(
          { length: currentYear - earliestYear + 1 },
          (_, i) => earliestYear + i,
        )
      : [currentYear];

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="hidden w-full max-w-[1400px] self-center lg:block">
        <TradesStatsPanel
          tableItems={tableItems}
          filterManager={filterManager}
          viewing={viewing}
        />
      </div>

      <Card className="card flex w-full max-w-[1400px] flex-col gap-5 self-center overflow-x-auto p-3 md:p-5">
        <CalendarHeader
          tradeYears={tradeYears}
          setViewing={setViewing}
          viewing={viewing}
          viewMonth={viewMonth}
          viewYear={viewYear}
          trades_by_date={trades_by_date}
        />
        <CalendarBody
          calendar={calendar}
          trades_by_date={trades_by_date}
          viewingMonth={viewing.getMonth()}
          viewingYear={viewing.getFullYear()}
        />
      </Card>
    </div>
  );
}
